import { Button, Flex, Modal, Typography } from 'antd';
import { usePatchAdminAccessMessageMutation } from 'api/AdminAccessMessagesApi';
import { Message } from 'api/MessagesApi';
import { useSelector } from 'react-redux';
import { closeModal, selectModalData, selectModalName } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import { logging } from 'utils/utils';
import './UpdateContactsModal.css';

export const UPDATE_CONTACTS_MODAL_NAME = 'UPDATE_CONTACTS_MODAL_NAME';

export type UpdateContactsModalProps = {
  adminAccessId: string;
  messageId: string;
  message: Message;
  senderName: string;
  oldContact: { id?: string; name: string; email: string; phone: string };
  newContact: { id?: string; name: string; email: string; phone: string };
  allContacts: { id?: string; name: string; email: string; phone: string }[];
  contactIndex: number;
};

function UpdateContactsModal({
  adminAccessId,
  messageId,
  message,
  senderName,
  oldContact,
  newContact,
  allContacts,
  contactIndex
}: UpdateContactsModalProps) {
  const dispatch = useAppDispatch();
  const [patchMessage, { isLoading: isPatching }] = usePatchAdminAccessMessageMutation();

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  const handleUpdateOne = async () => {
    try {
      const { id, ...newC } = newContact;
      const updatedContacts = allContacts.map((c, idx) => {
        return idx === contactIndex ? newC : c;
      });

      await patchMessage({
        id: adminAccessId,
        messageId: messageId,
        contacts: updatedContacts
      }).unwrap();

      handleCloseModal();
    } catch (e) {
      logging(e);
    }
  };

  const handleUpdateAll = async () => {
    try {
      const updatedContacts = allContacts.map((c, idx) => {
        return idx === contactIndex ? newContact : c;
      });

      await patchMessage({
        id: adminAccessId,
        messageId: messageId,
        contacts: updatedContacts
      }).unwrap();

      handleCloseModal();
    } catch (e) {
      logging(e);
    }
  };

  return (
    <Modal open={true} onCancel={handleCloseModal} footer={null} width={600} className="update-contacts-modal" centered>
      <div className="modal-header">
        <div className="modal-title">Update all messages for {oldContact.name}?</div>
        <Typography.Text type="secondary" style={{ fontSize: 16 }}>
          Would you like to update the contact information across all of them?
        </Typography.Text>
      </div>

      <Flex gap={12}>
        <Button block onClick={handleUpdateOne} loading={isPatching}>
          Update This Message Only
        </Button>
        <Button type="primary" block onClick={handleUpdateAll} loading={isPatching}>
          Update All Messages
        </Button>
      </Flex>
    </Modal>
  );
}

function UpdateContactsModalWrapper() {
  const modalName = useSelector(selectModalName);
  const modalData = useSelector(selectModalData);

  if (modalName !== UPDATE_CONTACTS_MODAL_NAME) {
    return null;
  }

  return (
    <UpdateContactsModal
      adminAccessId={modalData.adminAccessId}
      messageId={modalData.messageId}
      message={modalData.message}
      senderName={modalData.senderName}
      oldContact={modalData.oldContact}
      newContact={modalData.newContact}
      allContacts={modalData.allContacts}
      contactIndex={modalData.contactIndex}
    />
  );
}

export default UpdateContactsModalWrapper;
